import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    OnInit,
    Signal,
    signal,
    WritableSignal
} from "@angular/core";
import {AnalyticsService} from '../../logging/analytics.service';
import {Page} from "../../navigation/page";
import {NotificationService} from "../../notification/notification.service";
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {ButtonComponent} from "../../buttons/button/button.component";
import {ButtonProminence} from "../../buttons/button/button";
import {ReorderStatus} from "../reorder-status";
import {GroupOrderModel} from '../../group-order/group-order.model';
import {DisplayableOrderItem, OrderItem} from '../../order/order';
import {OrderEditPanelComponent} from '../../order/order-edit-panel/order-edit-panel.component';
import {OrderTotalsPanelComponent} from '../../order/order-totals-panel/order-totals-panel.component';
import {OrderInitiationStatus, OrderModel, OrderSubmissionStatus} from '../../order/order.model';
import {RecentOrderData, RecentOrderService} from '../../order/recent-order.service';
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {ValidationMessage} from "@fedelta-pos/data-client";
import {PaymentType} from '../../payment/payment';
import {PaymentModel} from '../../payment/payment.model';
import {QuickPayPanelComponent} from '../../payment/quick-pay-panel/quick-pay-panel.component';
import {ModalPageLayoutComponent} from '../../page-layout/modal-page-layout/modal-page-layout.component';
import {PageFooterControlDirective} from '../../page-layout/page-footer-control.directive';
import {IdentityModel} from '../../identity/identity.model';

@Component({
    selector: "woa-reorder-page",
    templateUrl: "reorder-page.component.html",
    styleUrl: "./reorder-page.component.scss",
    providers: [{provide: Page, useExisting: ReorderPageComponent}],
    imports: [
        ButtonComponent,
        ModalPageLayoutComponent,
        OrderEditPanelComponent,
        OrderTotalsPanelComponent,
        PageFooterControlDirective,
        QuickPayPanelComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class ReorderPageComponent extends Page implements OnInit, AfterViewInit {
    private groupOrderModel: GroupOrderModel = inject(GroupOrderModel);
    private notificationService: NotificationService = inject(NotificationService);
    private analyticsService: AnalyticsService = inject(AnalyticsService);
    private loadingService: LoadingService = inject(LoadingService);
    private orderModel: OrderModel = inject(OrderModel);
    private paymentModel: PaymentModel = inject(PaymentModel);
    private identityModel: IdentityModel = inject(IdentityModel);
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);

    ButtonProminence = ButtonProminence;

    public readonly paying: WritableSignal<boolean> = signal<boolean>(false);
    private readonly orderValidForCheckout: WritableSignal<boolean> = signal<boolean>(false);
    private readonly hasPreOrderItems: WritableSignal<boolean> = signal<boolean>(false);

    public readonly showQuickPay: Signal<boolean> = computed(() =>
        !this.paying() && !this.hasPreOrderItems() && this.orderValidForCheckout() && this.paymentModel.showQuickPay());

    private readonly itemTotal: Signal<number> = computed(() =>
        this.addedOrderItems ? this.addedOrderItems.map((item: OrderItem) => item.localTotalSignal())
            .reduce((amount: number, total: number) => total + amount, 0.0) : 0.0);

    private addedOrderItems: OrderItem[];
    private quickPayConfirmed: boolean = false;
    private recentOrder: RecentOrderData;
    private groupOrderValidationMessages: ValidationMessage[];
    private groupOrderError: any;

    constructor() {
        const recentOrderService: RecentOrderService = inject(RecentOrderService);

        super();
        this.recentOrder = recentOrderService.recentOrders[0];

        effect(async () => {
            if (this.orderModel.orderInitiationStatus() === OrderInitiationStatus.INITIATED) {
                if (this.notificationService.alertDisplayed()) {
                    this.orderValidForCheckout.set(false);
                } else {
                    const quickPayType: PaymentType = this.paymentModel.quickPayType();
                    if (quickPayType === undefined) {
                        this.orderValidForCheckout.set(false);
                    } else {
                        await this.orderingContextModel.untilExpectedCompletionEvaluationComplete();
                        this.orderValidForCheckout.set(await this.orderModel.validateOrderForPayment(quickPayType, false) && !this.orderingContextModel.fulfilmentDetailsRequired());
                    }
                }
            } else {
                this.orderValidForCheckout.set(false);
            }
        });

        effect(async (): Promise<void> => {
            if (this.orderModel.orderSubmissionStatus() === OrderSubmissionStatus.COMPLETE) {
                void this.dismiss(new ReorderStatus(true, this.quickPayConfirmed));
            } else if (this.orderModel.orderSubmissionStatus() === OrderSubmissionStatus.ORDER_INVALID || this.orderModel.orderSubmissionStatus() === OrderSubmissionStatus.ORDER_CANCELLED) {
                void this.dismiss(new ReorderStatus(false, false));
            }
        });
    }

    ngOnInit(): void {
        void this.load();
    }

    ngAfterViewInit(): void {
        if (!this.orderingContextModel.firstName()) {
            this.orderingContextModel.firstName.set(this.recentOrder.orderPlacement.firstName);
            this.orderingContextModel.firstNameTrusted.set(this.recentOrder.orderPlacement.nameTrusted);
        }
        if (!this.orderingContextModel.lastName()) {
            this.orderingContextModel.lastName.set(this.recentOrder.orderPlacement.lastName);
            this.orderingContextModel.lastNameTrusted.set(this.recentOrder.orderPlacement.nameTrusted);
        }
        if (!this.orderingContextModel.email()) {
            this.orderingContextModel.email.set(this.recentOrder.orderPlacement.email);
            this.orderingContextModel.emailTrusted.set(this.recentOrder.orderPlacement.emailTrusted);
        }
        if (!this.orderingContextModel.mobilePhone()) {
            this.orderingContextModel.mobilePhone.set(this.recentOrder.orderPlacement.mobilePhone);
            this.orderingContextModel.mobilePhoneTrusted.set(this.recentOrder.orderPlacement.mobilePhoneTrusted);
        }
    }

    override onDidDismissPage(status: ReorderStatus): void {
        if (!(status && status.ordered)) {
            if (this.orderModel.orderHasItems()) {
                this.orderModel.removeOrderItemsByIdentifier(this.addedOrderItems.map((item) => item.identifier));
                this.addedOrderItems = undefined;
            }
            if (!status) {
                void this.analyticsService.trackReorderRoundCancelled(this.itemTotal());
            }
        }
    }

    private async load(): Promise<void> {
        try {
            await this.loadingService.performWithSpinner(async () => {
                this.hasPreOrderItems.set(this.orderModel.orderHasItems());
                this.addedOrderItems = this.orderModel.getReorderableOrderItems()
                    .map((orderItem: OrderItem) => {
                        orderItem.proposed.set(true);
                        orderItem.dealIdSignal.set(undefined);
                        return orderItem;
                    })
                    .sort((a: OrderItem, b: OrderItem) => (a.transferRequestName ?? "").localeCompare(b.transferRequestName ?? ""));
                this.orderModel.addOrderItems(this.addedOrderItems);
                await this.analyticsService.trackReorderRoundShown(this.paymentModel.showQuickPay(), this.itemTotal());
            });
        } catch (error) {
            console.error(error);
        }
    }

    public async addToOrder(): Promise<void> {
        this.confirmOrderedItems(false);
        if (!!this.orderModel.totalTransfers() && !this.groupOrderModel.hasGroupOrder()) {
            await this.reinitiateGroupOrder();
        }
        void this.analyticsService.trackReorderRoundAdded(this.itemTotal());
        void this.dismiss(new ReorderStatus(true, false, this.recentOrder.orderInitiation.transferTargetName, this.groupOrderValidationMessages, this.groupOrderError));
    }

    public async quickPay(paymentType: PaymentType): Promise<void> {
        if (this.orderModel.orderInitiationStatus() !== OrderInitiationStatus.INITIATED) {
            return;
        }
        this.paying.set(true);
        await this.loadingService.performWithSpinner(async () => {
            this.confirmOrderedItems(true);
            await this.orderModel.untilOrderInitiationComplete();
        }, 30000);
        const paid: boolean = await this.orderModel.placeOrder(paymentType, this.groupOrderModel.preferredTransferTarget());
        if (paid) {
            void this.analyticsService.trackReorderRoundPaid(this.itemTotal());
        } else {
            this.paying.set(false);
        }
    }

    private confirmOrderedItems(quickPay: boolean): void {
        this.quickPayConfirmed = quickPay;

        // Removed proposed flag from new items, and set justOrdered flag to trigger promos
        this.addedOrderItems.forEach((item: OrderItem) => {
            item.justOrdered.set(!quickPay);
            if (item.justOrdered()) {
                // This will keep the item marked as justOrdered when initiation completes
                item.initiatedTotalSignal.set(undefined);
            }
            item.proposed.set(false);
        });

        // Request order initiation to ensure locally persisted order is up to date
        this.orderModel.requestOrderInitiation("reorder");
    }

    private async reinitiateGroupOrder(): Promise<void> {
        await this.loadingService.performWithSpinner(async () => {
            try {
                this.groupOrderValidationMessages = await this.groupOrderModel.saveGroupOrder(true, this.recentOrder.orderInitiation.transferTargetName || this.identityModel.currentIdentity().firstName);
            } catch (error) {
                this.groupOrderError = error;
            }
        }, undefined, "woa.message.group-order.creating");
    }

    public async orderItemRemoved(orderItem: DisplayableOrderItem): Promise<void> {
        this.addedOrderItems = this.addedOrderItems.filter((item: OrderItem) => item !== orderItem);
        if (!this.addedOrderItems.length) {
            void this.dismiss();
        }
    }
}
